import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useShadowRunDetail } from "../hooks/useShadowRunDetail";
import type { RunDetail, StatusBreakdown } from "../types";
import { SeverityTier, SEVERITY_TIER_ORDER } from "../types/severity-tier";
import {
  formatDateTime,
  formatDuration,
  formatNumber,
  formatPercentage,
} from "../utils/formatters";
import { getMatchRateLevel } from "../utils/colors";
import { StatusDistributionBar } from "../components/StatusDistributionBar/StatusDistributionBar";
import { LiveCounter } from "../components/LiveCounter/LiveCounter";
import { TopVarianceTable } from "../components/TopVarianceTable/TopVarianceTable";
import { ErrorSummaryPanel } from "../components/ErrorSummaryPanel/ErrorSummaryPanel";
import { ShardBreakdown } from "../components/ShardBreakdown/ShardBreakdown";
import styles from "./ShadowRunDetailScreen.module.css";

const STATUS_DOT_CLASS: Record<string, string> = {
  pending: "statusDotPending",
  running: "statusDotRunning",
  completed: "statusDotCompleted",
  failed: "statusDotFailed",
};

const LEVEL_CLASS: Record<string, string> = {
  excellent: "levelExcellent",
  good: "levelGood",
  warning: "levelWarning",
  critical: "levelCritical",
};

const QUALITY_LABELS: Record<string, string> = {
  critical: "Critical Success Rate",
  warning: "Low Success Rate",
};

/** Batch-specific tier labels: reframe "Match" → "Succeeded", "Error" → "Failed". */
const BATCH_TIER_LABELS: Partial<Record<SeverityTier, { label: string; className: string }>> = {
  [SeverityTier.MATCH]: { label: "Succeeded", className: "batchBadgeSucceeded" },
  [SeverityTier.ERROR]: { label: "Failed", className: "batchBadgeFailed" },
};

/** Derive status_breakdown from individual count fields when not provided by the API. */
function deriveStatusBreakdown(data: RunDetail): StatusBreakdown[] {
  if (data.status_breakdown && data.status_breakdown.length > 0) {
    return data.status_breakdown;
  }

  const total = data.total_packets;
  const entries: { tier: SeverityTier; count: number }[] = [
    { tier: SeverityTier.MATCH, count: data.match_count ?? 0 },
    { tier: SeverityTier.VARIANCE, count: data.variance_count ?? 0 },
    { tier: SeverityTier.ERROR, count: data.error_count ?? 0 },
  ];

  return entries
    .filter((e) => e.count > 0)
    .map((e) => ({
      tier: e.tier,
      count: e.count,
      percentage: total > 0 ? (e.count / total) * 100 : 0,
    }));
}

/** Compute duration in seconds from timestamps when duration_seconds is not provided. */
function deriveDurationSeconds(data: RunDetail): number | null {
  if (data.duration_seconds != null) return data.duration_seconds;
  if (data.started_at && data.completed_at) {
    const start = new Date(data.started_at).getTime();
    const end = new Date(data.completed_at).getTime();
    return (end - start) / 1000;
  }
  return null;
}

/** Derive match rate from available fields. */
function deriveMatchRate(data: RunDetail): number | null {
  if (data.match_rate != null) return data.match_rate;
  if (data.kpi_cards?.overall_match_rate != null)
    return data.kpi_cards.overall_match_rate;
  const completed = data.completed_count ?? 0;
  const matched = data.match_count ?? 0;
  if (completed > 0) return (matched / completed) * 100;
  return null;
}

/** Derive error rate from count fields. */
function deriveErrorRate(data: RunDetail): number {
  const errors = data.error_count ?? 0;
  const total = data.total_packets;
  if (total === 0) return 0;
  return (errors / total) * 100;
}

/** Derive completion rate from available fields. */
function deriveCompletionRate(data: RunDetail): number {
  if (data.kpi_cards?.completion_rate != null)
    return data.kpi_cards.completion_rate;
  const completed = data.completed_count ?? 0;
  const total = data.total_packets;
  if (total === 0) return 0;
  return (completed / total) * 100;
}

export function ShadowRunDetailScreen() {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useShadowRunDetail(batchId ?? "");

  const [varianceExpanded, setVarianceExpanded] = useState(false);
  const [errorsExpanded, setErrorsExpanded] = useState(false);
  const [shardsExpanded, setShardsExpanded] = useState(false);
  const [aiExpanded, setAiExpanded] = useState(false);
  const [detailsExpanded, setDetailsExpanded] = useState(false);

  if (!batchId) {
    return (
      <div className={styles.page} data-testid="run-detail-error">
        <nav className={styles.breadcrumb}>
          <Link to="/shadow-runs" className={styles.breadcrumbLink}>
            Shadow Runs
          </Link>
        </nav>
        <p className={styles.message}>
          Missing run identifier. Please navigate from the dashboard.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.page} data-testid="run-detail-loading">
        <nav className={styles.breadcrumb}>
          <Link to="/shadow-runs" className={styles.breadcrumbLink}>
            Shadow Runs
          </Link>
          <span className={styles.breadcrumbSeparator}>/</span>
          <span className={styles.breadcrumbCurrent}>{batchId}</span>
        </nav>
        <p className={styles.message}>Loading run details...</p>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className={styles.page} data-testid="run-detail-error">
        <nav className={styles.breadcrumb}>
          <Link to="/shadow-runs" className={styles.breadcrumbLink}>
            Shadow Runs
          </Link>
          <span className={styles.breadcrumbSeparator}>/</span>
          <span className={styles.breadcrumbCurrent}>{batchId}</span>
        </nav>
        <p className={styles.message}>Failed to load run {batchId}.</p>
      </div>
    );
  }

  const statusBreakdown = deriveStatusBreakdown(data);
  const durationSeconds = deriveDurationSeconds(data);
  const matchRate = deriveMatchRate(data);
  const errorRate = deriveErrorRate(data);
  const completionRate = deriveCompletionRate(data);

  const hasMatchRate = matchRate != null;
  const matchRateLevel = hasMatchRate ? getMatchRateLevel(matchRate) : null;
  const qualityLabel = matchRateLevel ? QUALITY_LABELS[matchRateLevel] : null;
  const levelClass = matchRateLevel ? styles[LEVEL_CLASS[matchRateLevel]] : undefined;

  const completedCount = data.completed_count ?? 0;
  const hasNoProcessedData = completedCount === 0;

  const tiersWithData = SEVERITY_TIER_ORDER.filter((tier) =>
    statusBreakdown.some((b) => b.tier === tier && b.count > 0),
  );

  const hasVariance =
    data.top_variance_components && data.top_variance_components.length > 0;
  const hasErrors = data.error_summary != null;
  const hasShards =
    data.shard_summaries != null && data.shard_summaries.length > 0;
  const hasAi = data.ai_summary != null;

  const packetsPath = `/shadow-runs/${batchId}/packets?org_category=${encodeURIComponent(data.org_category)}&org_id=${encodeURIComponent(data.org_id)}`;

  const handleComponentClick = (component: string) => {
    navigate(
      `${packetsPath}&component=${encodeURIComponent(component)}`,
    );
  };

  return (
    <div className={styles.page} data-testid="run-detail">
      {/* Breadcrumb */}
      <nav className={styles.breadcrumb}>
        <Link to="/shadow-runs" className={styles.breadcrumbLink}>
          Shadow Runs
        </Link>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span className={styles.breadcrumbCurrent}>{data.batch_id}</span>
      </nav>

      <div className={styles.container}>
        {/* 1. Status row */}
        <div className={styles.statusRow}>
          <span
            className={`${styles.statusDot} ${styles[STATUS_DOT_CLASS[data.status]] ?? ""}`}
          />
          <span className={styles.statusLabel}>{data.status}</span>
          {qualityLabel && !hasNoProcessedData && (
            <>
              <span className={styles.statusSeparator}>|</span>
              <span className={`${styles.qualityDot} ${levelClass}`} />
              <span className={`${styles.qualityLabel} ${levelClass}`}>
                {qualityLabel}
              </span>
            </>
          )}
        </div>

        {/* 2. Hero metric */}
        <div className={styles.heroMetric} data-testid="hero-metric">
          {!hasMatchRate || hasNoProcessedData ? (
            <>
              <span className={styles.heroValueNeutral}>--%</span>
              <span className={styles.heroLabel}>Success Rate</span>
            </>
          ) : (
            <>
              <div className={styles.heroRow}>
                <span className={`${styles.heroValue} ${levelClass}`}>
                  {formatPercentage(matchRate)}
                </span>
                <span className={`${styles.heroLevel} ${levelClass}`}>
                  {matchRateLevel}
                </span>
              </div>
              <span className={styles.heroLabel}>Success Rate</span>
              {durationSeconds != null && (
                <span className={styles.heroDuration}>
                  {formatDuration(durationSeconds)}
                </span>
              )}
            </>
          )}
        </div>

        {/* 3. Progress bar */}
        <div className={styles.progressArea}>
          <StatusDistributionBar breakdown={statusBreakdown} height={24} />
          {qualityLabel && !hasNoProcessedData && (
            <div className={`${styles.progressAccent} ${levelClass}`} />
          )}
          <span className={styles.packetCount}>
            {formatNumber(completedCount)} /{" "}
            {formatNumber(data.total_packets)} packets
          </span>
        </div>

        {/* 4. Stats strip */}
        <div className={styles.statsStrip}>
          <LiveCounter
            value={completedCount}
            label="Processed"
            size="small"
          />
          <LiveCounter
            value={errorRate}
            label="Error Rate"
            formatter={formatPercentage}
            size="small"
          />
          <LiveCounter
            value={completionRate}
            label="Completion Rate"
            formatter={formatPercentage}
            size="small"
          />
          {data.kpi_cards?.completed_match_rate != null && (
            <LiveCounter
              value={data.kpi_cards.completed_match_rate}
              label="Completed Success Rate"
              formatter={formatPercentage}
              size="small"
            />
          )}
        </div>

        {/* 5. Packet breakdown */}
        {tiersWithData.length > 0 && (
          <div className={styles.tierSection}>
            <h3 className={styles.sectionTitle}>Packet Breakdown</h3>
            <div className={styles.tierList}>
              {tiersWithData.map((tier) => {
                const breakdown = statusBreakdown.find(
                  (b) => b.tier === tier,
                );
                if (!breakdown) return null;
                const batchTier = BATCH_TIER_LABELS[tier];
                return (
                  <div key={tier} className={styles.tierRow}>
                    <span className={styles.tierBadge}>
                      {batchTier ? (
                        <span
                          className={`${styles.batchBadge} ${styles[batchTier.className]}`}
                        >
                          {batchTier.label}
                        </span>
                      ) : (
                        <span className={styles.batchBadge}>{tier}</span>
                      )}
                    </span>
                    <span className={styles.tierCount}>
                      {formatNumber(breakdown.count)}
                    </span>
                    <span className={styles.tierPercentage}>
                      ({formatPercentage(breakdown.percentage)})
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 6. Top Variance Components (collapsible) */}
        {hasVariance && (
          <div className={styles.detailsSection}>
            <button
              className={styles.collapseToggle}
              onClick={() => setVarianceExpanded(!varianceExpanded)}
              type="button"
              aria-expanded={varianceExpanded}
              aria-controls="section-variance"
            >
              Top Variance Components
              <span className={styles.toggleIcon} aria-hidden="true">
                {varianceExpanded ? "\u25B2" : "\u25BC"}
              </span>
            </button>
            {varianceExpanded && (
              <div id="section-variance">
                <TopVarianceTable
                  components={data.top_variance_components!}
                  onComponentClick={handleComponentClick}
                />
              </div>
            )}
          </div>
        )}

        {/* 7. Error Summary (collapsible) */}
        {hasErrors && (
          <div className={styles.detailsSection}>
            <button
              className={styles.collapseToggle}
              onClick={() => setErrorsExpanded(!errorsExpanded)}
              type="button"
              aria-expanded={errorsExpanded}
              aria-controls="section-errors"
            >
              Error Summary
              <span className={styles.toggleIcon} aria-hidden="true">
                {errorsExpanded ? "\u25B2" : "\u25BC"}
              </span>
            </button>
            {errorsExpanded && (
              <div id="section-errors">
                <ErrorSummaryPanel errorSummary={data.error_summary!} />
              </div>
            )}
          </div>
        )}

        {/* 8. Shard Breakdown (collapsible) */}
        {hasShards && (
          <div className={styles.detailsSection}>
            <button
              className={styles.collapseToggle}
              onClick={() => setShardsExpanded(!shardsExpanded)}
              type="button"
              aria-expanded={shardsExpanded}
              aria-controls="section-shards"
            >
              Shard Breakdown
              <span className={styles.toggleIcon} aria-hidden="true">
                {shardsExpanded ? "\u25B2" : "\u25BC"}
              </span>
            </button>
            {shardsExpanded && (
              <div id="section-shards">
                <ShardBreakdown shards={data.shard_summaries!} />
              </div>
            )}
          </div>
        )}

        {/* 9. AI Analysis (collapsible) */}
        {hasAi && (
          <div className={styles.detailsSection}>
            <button
              className={styles.collapseToggle}
              onClick={() => setAiExpanded(!aiExpanded)}
              type="button"
              aria-expanded={aiExpanded}
              aria-controls="section-ai"
            >
              AI Analysis
              <span className={styles.toggleIcon} aria-hidden="true">
                {aiExpanded ? "\u25B2" : "\u25BC"}
              </span>
            </button>
            {aiExpanded && (
              <div id="section-ai" className={styles.aiSummary}>
                {data.ai_summary}
              </div>
            )}
          </div>
        )}

        {/* 10. Run Details (collapsible) — always present */}
        <div className={styles.detailsSection}>
          <button
            className={styles.collapseToggle}
            onClick={() => setDetailsExpanded(!detailsExpanded)}
            type="button"
            aria-expanded={detailsExpanded}
            aria-controls="section-details"
          >
            Run Details
            <span className={styles.toggleIcon} aria-hidden="true">
              {detailsExpanded ? "\u25B2" : "\u25BC"}
            </span>
          </button>
          {detailsExpanded && (
            <dl id="section-details" className={styles.detailsList}>
              <dt className={styles.detailsLabel}>Run ID</dt>
              <dd className={styles.detailsValue}>{data.batch_id}</dd>
              {data.org_id && (
                <>
                  <dt className={styles.detailsLabel}>Organisation</dt>
                  <dd className={styles.detailsValue}>{data.org_id}</dd>
                </>
              )}
              {durationSeconds != null && (
                <>
                  <dt className={styles.detailsLabel}>Duration</dt>
                  <dd className={styles.detailsValue}>
                    {formatDuration(durationSeconds)}
                  </dd>
                </>
              )}
              {data.started_at && (
                <>
                  <dt className={styles.detailsLabel}>Started</dt>
                  <dd className={styles.detailsValue}>
                    {formatDateTime(data.started_at)}
                  </dd>
                </>
              )}
              {data.completed_at && (
                <>
                  <dt className={styles.detailsLabel}>Completed</dt>
                  <dd className={styles.detailsValue}>
                    {formatDateTime(data.completed_at)}
                  </dd>
                </>
              )}
              {data.created_at && (
                <>
                  <dt className={styles.detailsLabel}>Created</dt>
                  <dd className={styles.detailsValue}>
                    {formatDateTime(data.created_at)}
                  </dd>
                </>
              )}
            </dl>
          )}
        </div>

        {/* CTA — View Packets */}
        {data.status === "completed" && (
          <button
            type="button"
            className={styles.ctaButton}
            onClick={() => navigate(packetsPath)}
          >
            View Packets
          </button>
        )}
      </div>
    </div>
  );
}
